import { batchActions } from 'redux-batched-actions';
import { createAction } from 'redux-actions';
import update from 'immutability-helper';
import {
  initialize,
  destroy,
  clearSubmitErrors,
  change as changeForm,
  getFormValues,
  arrayPop,
  arrayPush,
  formValueSelector,
} from 'redux-form';

import { get, includes, last, map, omit, set } from 'lodash';
import { openModalDialog, closeModalDialog } from '@nextiva/crm-modals';
import { resetRichTextEditor } from '@nextiva/rich-text-editor';
import { showSuccessNotification, showErrorNotification } from '@nextiva/engagement-ui';
import { questionTypes } from 'sharedParts';
import { pickCacheableFields } from 'config/blockCacheConfig';
import Constants from 'constants/actions';
import stickyButtonsConfig from 'config/stickyButtonsConfig';

import createRawBlock from 'utils/createRawBlock';
import validateRedirectUrl from 'utils/validateRedirectUrl';
import numbering from 'sharedParts/utils/numbering';
import getUrl from 'utils/getUrl';
import formNames from 'config/formNames';
import generateEditToolFormName from 'utils/generateEditToolFormName';
import isValidApiId from 'utils/isValidApiId';
import questionLogicUtils, { getQuestionToJumpUuid } from 'utils/questionLogicUtils';
import generateEditLogicToolFormName from 'utils/generateEditLogicToolFormName';

import {
  canMoveSurveyEditorBlock,
  canOpenPostSurveyLogic,
  doesLastQuestionHaveValidLogic,
  getActiveEditorSection,
  getActiveEntityCollectionPath,
  getActiveSectionBlockMaxAvailableIndex,
  getActiveSectionBlocks,
  getActiveSectionBlockUuidByIndex,
  getActiveSectionOrderMap,
  getActiveSurveySettings,
  getBlockByUuid,
  getBlockIndexInActiveSection,
  getBlockOptionIdByIndex,
  getBlockType,
  getCachedBlockData,
  getCurrentRouterRootPath,
  getUserTimeZone,
  getEditorEntityId,
  getEditorHeaderData,
  getEditorOrderedBlockList,
  getEditorRawSurveyData,
  getEditorSectionHeader,
  getEditorSurveyName,
  getEditToolFormNames,
  getInlineEditToolFormNames,
  getInlineLabelEditorFormNames,
  getLogicEditToolFormNames,
  getNetworkPendingBlockList,
  getNetworkPendingOrderMap,
  getNextBlockUuidInActiveSection,
  getOrderMapBySection,
  getPageToStartPreview,
  getQuestionLogic,
  getQuestionLogicList,
  getQuestionOptionsByUuid,
  getQuestionRegardlessLogicUuid,
  getQuestionsSectionBlocks,
  getSurveyPreviewLatestVisitedPageNumber,
  getSurveyRedirectUrl,
  getSurveySlug,
  getSurveyTakerUrl,
  isActiveSectionRequiredBlockNeeded,
  isAddBlockNetworkPending,
  isMoveBlockNetworkPending,
  isQuestionLogicValid,
  isQuestionUsedInOtherQuestionLogic,
  isSurveyBackButtonEnabled,
  isSurveyLandingEnabled,
  isSurveyThankYouEnabled,
  wasQuestionModifiedButNotPersisted,
} from 'reducers';
import {
  openChangeScaleItemCount,
  openDeleteOptionWithLogicModal,
  openRemoveBlockWithLogicModal,
} from 'actions/skipLogicActions';
import {
  restorePreview,
  setCurrentPreviewPage,
  setSectionHeaders as setPreviewSectionHeaders,
  setSurveyBlockList as setPreviewSurveyBlockList,
  setSurveyInitialSettings as setPreviewSurveySettings,
  setSharedHeader as setPreviewSurveySharedHeader,
  setQuestionNumbers,
} from 'actions/surveyPreviewActions';
import { navigate, openApplicationErrorModal, copyLinkToClipboard } from 'actions/appActions';
import { handleApiErrorsLegacy, handleApiErrors } from 'actions/actionsUtils';
import { setSectionHeaders, applySectionHeaders, removeSectionHeader } from 'actions/headerEditorActions';

import mapEditorSectionToUrl from 'config/mapEditorSectionToUrl';
import blockConfig, { getQuestionConfig, getBlockDefaultProps } from 'sharedParts/config/blockConfig';
import api from 'api';
import modalTypes from 'constants/AppModalTypes';
import isPageBreak from 'utils/isPageBreak';
import cloneBlock from 'utils/cloneBlock';
import generateLabelItem from 'utils/generateLabelItem';
import { surveyEditorRoot, surveyTemplateEditorRoot } from '../routes/paths';
import { parsedData } from './actionsUtils';
import generateInlineLabelEditorFormName from '../utils/generateInlineLabelEditorFormName';
import { SURVEY_TEMPLATE_TYPE } from '../constants/SurveyTypes';
import { editorPath } from '../config/baseUrl';
import stubMorphedQuestionByType from '../components/questions/utils/stub/stubMorphedQuestionByType';
import DefaultSurveySettings from '../config/DefaultSurveySettings';
import triggerScrollEvent from '../utils/triggerScrollEvent';
import { syncPreviewThumbnail } from '../domains/previewThumbnail/actions';
import { setReportSettingsTimezone } from '../utils/setReportSettingsTimezone';
import { resetSurveyEditorPersistedState } from './surveyPreviewActions';
import generateInlineEditToolFormName from '../domains/blockEditor/utils/generateInlineEditToolFormName';

export const updateBlockListBulk = createAction(Constants.UPDATE_BLOCK_LIST_BULK);

export const addBlockToPendingQueue = createAction(
  Constants.FILL_PENDING_BLOCK_LIST,
  (block, sectionName) => ({ block, sectionName }),
);
export const addModifiedNewNotPersistedBlockUuid = createAction(
  Constants.ADD_MODIFIED_NEW_NOT_PERSISTED_BLOCK_UUID,
);
export const addNewNotPersistedBlockUuid = createAction(Constants.ADD_NEW_NOT_PERSISTED_BLOCK_UUID);
export const cacheBlockData = createAction(Constants.CACHE_BLOCK_DATA, (uuid, data) => ({ uuid, data }));
export const changeSurveySetting = createAction(Constants.CHANGE_SURVEY_SETTING, (name, value) =>
  set({}, name, value),
);
export const changeSurveySettings = createAction(Constants.CHANGE_SURVEY_SETTING);
export const clearNetworkPendingOrderMap = createAction(Constants.CLEAR_NETWORK_PENDING_ORDER_MAP);
export const clearPendingBlockList = createAction(Constants.CLEAR_PENDING_BLOCK_LIST);
export const clearSection = createAction(Constants.CLEAR_SURVEY_SECTION);
export const deleteBlock = createAction(Constants.DELETE_BLOCK);
export const finishBlockOrderPersistence = createAction(Constants.FINISH_BLOCK_ORDER_PERSISTENCE);
export const finishRemoveBlock = createAction(Constants.FINISH_REMOVE_BLOCK);
export const finishUpdateBlockList = createAction(Constants.FINISH_UPDATE_BLOCK_LIST);
export const handleSurveyRedirectUrlChange = createAction(Constants.SET_REDIRECT_URL);
export const insertBlockItem = createAction(Constants.INSERT_BLOCK, (block, index) => ({ block, index }));
export const moveBlock = createAction(Constants.MOVE_BLOCK, (clientUuid, newIndex) => ({
  clientUuid,
  newIndex,
}));
export const persistBlockUpdateRequest = createAction(
  Constants.PERSIST_BLOCK_UPDATE_REQUEST,
  (clientUuid, updatedBlock) => ({ clientUuid, updatedBlock }),
);
export const removeCachedBlockData = createAction(Constants.REMOVE_CACHED_BLOCK_DATA);
export const removeModifiedNewNotPersistedBlockUuid = createAction(
  Constants.REMOVE_MODIFIED_NEW_NOT_PERSISTED_BLOCK_UUID,
);
export const removeNewNotPersistedBlockUuid = createAction(Constants.REMOVE_NEW_NOT_PERSISTED_BLOCK_UUID);
export const removeUnsavedBlocks = createAction(Constants.REMOVE_UNSAVED_BLOCK_LIST);
export const replaceSurveyBlock = createAction(Constants.REPLACE_SURVEY_BLOCK);
export const resetPostSurveyLogic = createAction(Constants.RESET_POST_SURVEY_LOGIC);
export const returnUndeleteBlockList = createAction(
  Constants.RETURN_UNDELETE_BLOCK_LIST,
  (sectionName, blockList) => ({ sectionName, blockList }),
);
export const setOverwriteCustomStyles = createAction(Constants.SET_OVERWRITE_CUSTOM_STYLES);
export const setActiveBlockClientUuid = createAction(Constants.SET_ACTIVE_BLOCK_CLIENT_UUID);
export const setActiveSurvey = createAction(Constants.SET_EDITOR_ACTIVE_SURVEY);
export const setBlockTouched = createAction(Constants.SET_ACTIVE_BLOCK_TOUCHED_FLAG);
export const setEditorLoadingState = createAction(Constants.SET_EDITOR_LOADING_STATE);
export const setIsActiveBlockLogicPanelOpened = createAction(
  Constants.SET_IS_ACTIVE_BLOCK_LOGIC_PANEL_OPENED,
);
export const setIsSurveyNameFocused = createAction(Constants.SET_IS_SURVEY_NAME_FOCUSED);
export const setLatestAddedBlockUuid = createAction(Constants.SET_LATEST_ADDED_BLOCK_CLIENT_UUID);
export const setNetworkAddBlockPendingStatus = createAction(Constants.SET_ADD_BLOCK_NETWORK_PENDING_STATUS);
export const setNetworkPendingOrderMap = createAction(Constants.SET_NETWORK_PENDING_ORDER_MAP);
export const setNetworkPendingOrderMapStatus = createAction(Constants.SET_NETWORK_PENDING_ORDER_MAP_STATUS);
export const setSharedHeader = createAction(Constants.SET_SHARED_HEADER);
export const setSurveyLandingPageEnabled = createAction(Constants.SET_LANDING_PAGE_ENABLED_STATE);
export const setSurveyThankYouPageEnabled = createAction(Constants.SET_THANK_YOU_PAGE_ENABLED_STATE);
export const setSurveyHeaderEndEnabled = createAction(Constants.SET_SURVEY_HEADER_END_ENABLED);
export const setSurveyHeaderLandingEnabled = createAction(Constants.SET_SURVEY_HEADER_LANDING_ENABLED);
export const setSurveyHeaderQuestionsEnabled = createAction(Constants.SET_SURVEY_HEADER_QUESTIONS_ENABLED);
export const sortBlocksByOrderMap = createAction(Constants.SORT_SECTION_BLOCK_LIST_BY_ORDER_MAP);
export const startRemoveBlock = createAction(Constants.START_REMOVE_BLOCK);
export const startUpdateBlockList = createAction(
  Constants.START_UPDATE_BLOCK_LIST,
  (blockList, sectionName) => ({ blockList, sectionName }),
);
export const toggleSurveyFullWidth = createAction(Constants.TOGGLE_SURVEY_FULL_WIDTH);
export const updateActiveSection = createAction(Constants.SET_EDITOR_ACTIVE_SECTION);
export const updateBlock = createAction(Constants.UPDATE_BLOCK, (index, blockData) => ({ index, blockData }));
export const updateBlockRequest = createAction(Constants.UPDATE_BLOCK_REQUEST, (index, blockData) => ({
  index,
  blockData,
}));
export const updateOrderMap = createAction(Constants.UPDATE_ORDER_MAP);
export const updateShareUrlSlug = createAction(Constants.SET_SHARE_URL_SLUG);

export const persistSurveySettings = (settings) => (dispatch, getState) => {
  const state = getState();

  return api
    .changeSurveySetting(getActiveEntityCollectionPath(state), getEditorEntityId(state), settings)
    .catch((rejection) => handleApiErrorsLegacy(rejection, dispatch));
};

export const persistSurveySetting = (name, value) => persistSurveySettings(set({}, name, value));

export const persistBlockUpdate = (uuid, blockData) => (dispatch, getState) => {
  dispatch(updateOrderMap());
  const state = getState();
  const { id: itemId } = blockData;

  if (!itemId) {
    dispatch(addModifiedNewNotPersistedBlockUuid(uuid));
    return;
  }

  const { logic: { action = '' } = { action: '' } } = blockData;

  return api
    .updateBlock(getActiveEntityCollectionPath(state), getEditorEntityId(state), {
      blockData: parsedData(blockData, action),
      itemId,
      orderMap: getActiveSectionOrderMap(state),
      sectionName: getActiveEditorSection(state),
    })
    .catch((rejection) => handleApiErrorsLegacy(rejection, dispatch));
};

const saveModifiedNotPersistedBlockList = (blockList) => (dispatch, getState) => {
  Object.keys(blockList).forEach((uuid) => {
    const state = getState();
    if (wasQuestionModifiedButNotPersisted(state, uuid)) {
      dispatch(persistBlockUpdateRequest(uuid, getBlockByUuid(state, uuid))).then(() => {
        dispatch(removeModifiedNewNotPersistedBlockUuid(uuid));
      });
    }
  });
};

/* persistBlockList - Saving block. Recursively call itself if there is pending blocks
 *
 *  arguments:
 *    surveyId - (integer)
 *    blockList - (array) - array of items to save
 */
export const persistBlockList = (entityId, blockList, sectionName) => (dispatch, getState) => {
  const state = getState();
  dispatch(batchActions([setNetworkAddBlockPendingStatus(true), updateOrderMap()]));

  const activeSectionOrderMap = getOrderMapBySection(getState(), sectionName);
  const uuidList = blockList.map((item) => item.clientUuid);

  return api
    .saveBlockList(getActiveEntityCollectionPath(state), entityId, {
      blockList,
      sectionName,
      activeSectionOrderMap,
    })
    .catch((rejection) => {
      dispatch(clearPendingBlockList(sectionName));
      dispatch(setNetworkAddBlockPendingStatus(false));
      dispatch(removeUnsavedBlocks(uuidList));
      dispatch(updateOrderMap());
      return handleApiErrorsLegacy(rejection, dispatch);
    })
    .then((savedBlockList) => {
      dispatch(
        batchActions([
          startUpdateBlockList(savedBlockList, sectionName),
          setNetworkAddBlockPendingStatus(false),
        ]),
      );
      dispatch(saveModifiedNotPersistedBlockList(savedBlockList));

      // Settings pending network status @sinslav
      Object.entries(getNetworkPendingBlockList(getState())).forEach(([section, pendingSectionBlockList]) => {
        if (pendingSectionBlockList.length) {
          dispatch(persistBlockList(entityId, pendingSectionBlockList, section));
          dispatch(clearPendingBlockList(section));
        }
      });
    })
    .finally(() => {
      dispatch(finishUpdateBlockList());
      dispatch(removeNewNotPersistedBlockUuid(uuidList));
    });
};

export const insertBlock = (block, index, scrollToBlock = true) => (dispatch) => {
  dispatch(
    batchActions([
      setLatestAddedBlockUuid(scrollToBlock ? block.clientUuid : null),
      insertBlockItem(block, index),
      updateOrderMap(),
      setQuestionNumbers(),
    ]),
  );
};

/* smartAddBlock - always calling insert action (to web view) for smooth UX and decide what to do: immediate
 * call persistBlockList or gather (buffer) blocks if saving is in progress to do batch save
 *
 *  arguments:
 *    block - (object)-  describe whole block including uuid, but NOT including real id (see persistBlockList)
 *    [index] - (number) - place (index) to insert block, inserts to end if not provided
 */
export const smartAddBlock = (block, index, { scrollToBlock } = { scrollToBlock: true }) => {
  return (dispatch, getState) => {
    const state = getState();
    const blockSettings = blockConfig[block.type].settings;
    const activeSectionName = getActiveEditorSection(state);
    const entityId = getEditorEntityId(state);
    const isUniqueBlock = !!blockSettings.isUniqueField(activeSectionName);
    const isInSection = getActiveSectionBlocks(state).some((item) => item.type === block.type);
    if (isUniqueBlock && isInSection) {
      return;
    }

    const activeSectionLength = getActiveSectionBlocks(state).length;
    const isValidIndex = Number.isInteger(index) && index < activeSectionLength;
    // if section is ThankYouPage - by default insert at the end

    const indexFromRight = getActiveSectionBlockMaxAvailableIndex(state);
    const defaultIndex = activeSectionLength < 1 ? 0 : indexFromRight;
    const validIndex = isValidIndex ? index : defaultIndex;
    dispatch(insertBlock(block, validIndex, scrollToBlock));
    dispatch(addNewNotPersistedBlockUuid(block.clientUuid));

    if (isAddBlockNetworkPending(getState()) === true) {
      dispatch(addBlockToPendingQueue(block, activeSectionName));
    } else {
      dispatch(persistBlockList(entityId, [block], activeSectionName));
    }
  };
};

/*
 *  addSectionRequiredBlock - creating requiredBlock (e.g. Submit, Next) for each section (if there no one)
 */
export const addSectionRequiredBlock = () => {
  return (dispatch, getState) => {
    const state = getState();
    const requiredBlock = createRawBlock(stickyButtonsConfig[getActiveEditorSection(state)]);
    dispatch(smartAddBlock(requiredBlock, 0));
  };
};

/* addBlock - add brand new block to active survey to its active section. Recursively call
 * itself if need to insert section required block
 *  arguments:
 *    itemType - (string) - type of new question
 *    [index] - (number) - optional, place (index) to insert block
 */
export const addBlock = (itemType, index, config) => {
  return (dispatch, getState) => {
    const state = getState();
    // insert required button if empty section needs it
    if (isActiveSectionRequiredBlockNeeded(state)) {
      dispatch(addSectionRequiredBlock());
      dispatch(addBlock(itemType, index, config));
      return;
    }

    const newBlock = createRawBlock(itemType);
    dispatch(smartAddBlock(newBlock, index, config));

    const hasLastQuestionValidLogic = doesLastQuestionHaveValidLogic(state);
    if (hasLastQuestionValidLogic && itemType !== questionTypes.nextButton) {
      const nextButtonIndex = getActiveSectionBlockMaxAvailableIndex(state);
      dispatch(
        addBlock(questionTypes.nextButton, nextButtonIndex, {
          scrollToBlock: false,
        }),
      );
    }
  };
};

export const handleDuplicateLogicNextButton = (uuid, index) => (dispatch, getState) => {
  const state = getState();
  const defaultNextConfig = getQuestionConfig(questionTypes.nextButton);
  const defaultSubmitLabel = getQuestionConfig(questionTypes.submitButton).getDefaultProps().label;

  const nextButtonUuid = getNextBlockUuidInActiveSection(state, uuid);
  const clonedButton = cloneBlock(getBlockByUuid(state, nextButtonUuid));

  const clonedNextLabel =
    clonedButton.label === defaultSubmitLabel
      ? defaultNextConfig.getDefaultProps().label
      : clonedButton.label;

  const clonedNext = {
    ...clonedButton,
    type: defaultNextConfig.type,
    label: clonedNextLabel,
  };

  dispatch(smartAddBlock(clonedNext, index));
};

/* duplicateBlock - create a copy on block, inserting right after block that user trying to duplicate,
 *  providing new clientUuid and deleting 'id' prop for duplicated block
 *  arguments:
 *    uuid - (number) - block clientUuid
 */
export const duplicateBlock = (uuid) => {
  return (dispatch, getState) => {
    const state = getState();
    const clonedBlock = cloneBlock(getBlockByUuid(state, uuid));
    let index = getBlockIndexInActiveSection(state, uuid) + 1;

    const activeSection = getActiveEditorSection(state);
    const isUniqueBlock = !!blockConfig[clonedBlock.type].settings.isUniqueField(activeSection);
    if (isUniqueBlock) {
      // @TODO Show some error here @sinslav
      return;
    }

    if (isQuestionLogicValid(state, uuid)) {
      dispatch(handleDuplicateLogicNextButton(uuid, index));
      index++;
    }

    dispatch(smartAddBlock(clonedBlock, index));
    dispatch(setActiveBlockClientUuid(clonedBlock.clientUuid));
    dispatch(setQuestionNumbers());
  };
};

// List of forms, that has { destroyOnUnmount: false } @sinslav
export const getNonSelfDestroyableEditToolForms = (uuid) => [
  generateEditToolFormName(uuid),
  generateEditLogicToolFormName(uuid),
  generateInlineEditToolFormName(uuid),
  generateInlineLabelEditorFormName(uuid),
];

export const destroyQuestionEditToolForms = (uuid) => destroy(...getNonSelfDestroyableEditToolForms(uuid));

export const removeBlock = (uuid) => (dispatch, getState) => {
  const state = getState();
  const block = getBlockByUuid(state, uuid);
  const blockType = getBlockType(state, uuid);
  const entityId = getEditorEntityId(state);
  const index = getActiveSectionOrderMap(state)[uuid];
  const validIndex = index >= 0 ? index : 0;

  dispatch(startRemoveBlock());
  const activeSection = getActiveEditorSection(state);
  if (getQuestionConfig(blockType).settings.isRequiredField(activeSection)) {
    return;
  }

  dispatch(
    batchActions([
      deleteBlock(uuid),
      removeCachedBlockData(uuid),
      setActiveBlockClientUuid(null),
      updateOrderMap(),
      destroyQuestionEditToolForms(uuid),
    ]),
  );

  if (!isValidApiId(block.id)) {
    return;
  }
  return api
    .deleteBlock(getActiveEntityCollectionPath(state), entityId, {
      blockToRemoveId: block.id,
    })
    .catch((rejection) => {
      dispatch(setActiveBlockClientUuid(uuid));
      dispatch(insertBlock(block, validIndex));

      return handleApiErrorsLegacy(rejection, dispatch);
    })
    .finally(() => dispatch(finishRemoveBlock()));
};

export const handleRemoveBlock = (questionUuid) => (dispatch, getState) => {
  const state = getState();
  const hasLogic = isQuestionLogicValid(state, questionUuid);
  const isInsideOtherLogicQuestion = isQuestionUsedInOtherQuestionLogic(state, questionUuid);

  if (hasLogic || isInsideOtherLogicQuestion) {
    dispatch(openRemoveBlockWithLogicModal(questionUuid, isInsideOtherLogicQuestion));
    return;
  }

  dispatch(removeBlock(questionUuid));
  dispatch(setQuestionNumbers());
};

export const unsetActiveBlock = () => setActiveBlockClientUuid(null);

/* persistOrderMap - Saving block order. Recursively call itself if there is pending order map
 *
 *  arguments:
 *    orderMap - (object) - map of block order in format { uuid : index, ... }
 *    fallBackOrderMap - (object) - old orderMap to revert changes in case network is down
 */
export const persistOrderMap = (orderMap, fallBackOrderMap) => (dispatch, getState) => {
  const state = getState();
  const entityId = getEditorEntityId(state);
  const section = getActiveEditorSection(state);
  dispatch(batchActions([setNetworkPendingOrderMapStatus(true), updateOrderMap()]));
  return api
    .updateSectionBlockOrder(getActiveEntityCollectionPath(state), entityId, {
      section,
      orderMap,
    })
    .catch((rejection) => {
      dispatch(
        batchActions([
          sortBlocksByOrderMap(fallBackOrderMap),
          clearNetworkPendingOrderMap(),
          setNetworkPendingOrderMapStatus(false),
        ]),
      );
      return handleApiErrorsLegacy(rejection, dispatch);
    })
    .then(() => {
      const pendingOrderMap = getNetworkPendingOrderMap(getState());
      dispatch(setNetworkPendingOrderMapStatus(false));
      if (pendingOrderMap) {
        dispatch(clearNetworkPendingOrderMap());
        dispatch(persistOrderMap(pendingOrderMap));
      } else {
        dispatch(finishBlockOrderPersistence());
      }
    });
};

/* persistMoveBlock - always calling move action (to web view, if it's allowed by business logic)
 * for smooth UX and decide what to do: immediate call persistOrderMap or saving latest changes
 * if saving is in progress and then do only ONE persistOrderMap with latest changes
 *
 *  arguments:
 *    clientUuid - (number)
 *    newIndex - (number) - place (index) to insert block
 */
export const persistMoveBlock = (clientUuid, newIndex) => {
  return (dispatch, getState) => {
    dispatch(updateOrderMap());
    const state = getState();

    if (!canMoveSurveyEditorBlock(state, clientUuid, newIndex)) {
      return;
    }

    dispatch(batchActions([moveBlock(clientUuid, newIndex), updateOrderMap(), setQuestionNumbers()]));

    if (isMoveBlockNetworkPending(getState())) {
      dispatch(setNetworkPendingOrderMap(getActiveSectionOrderMap(getState())));
    } else {
      const fallBackOrderMap = getActiveSectionOrderMap(getState());
      dispatch(persistOrderMap(getActiveSectionOrderMap(getState()), fallBackOrderMap));
    }
  };
};

export const moveBlockToIndex = ({ clientUuid, newIndex }) => (dispatch) => {
  dispatch(persistMoveBlock(clientUuid, newIndex));
};

export const moveBlockUp = (clientUuid) => (dispatch, getState) => {
  const state = getState();
  const currentBlockIndex = getBlockIndexInActiveSection(state, clientUuid);
  const prevNonNextBlockUuid = getActiveSectionBlockUuidByIndex(state, currentBlockIndex - 2);
  const isPrevBlockValidLogic = isQuestionLogicValid(state, prevNonNextBlockUuid);
  const newIndex = isPrevBlockValidLogic ? currentBlockIndex - 2 : currentBlockIndex - 1;
  return dispatch(moveBlockToIndex({ clientUuid, newIndex }));
};

export const moveBlockDown = (clientUuid) => (dispatch, getState) => {
  const state = getState();
  const currentBlockIndex = getBlockIndexInActiveSection(state, clientUuid);
  const nextBlockUuid = getActiveSectionBlockUuidByIndex(state, currentBlockIndex + 1);
  const isNextBlockValidLogic = isQuestionLogicValid(state, nextBlockUuid);
  const newIndex = isNextBlockValidLogic ? currentBlockIndex + 2 : currentBlockIndex + 1;
  return dispatch(moveBlockToIndex({ clientUuid, newIndex }));
};

export const handleAddLogicNext = (clientUuid, logicQuestionIndex, updatedBlock = {}) => (
  dispatch,
  getState,
) => {
  const state = getState();
  const isLogicValid = isQuestionLogicValid(state, clientUuid);
  if (isLogicValid) {
    const nextBlockUuid = getNextBlockUuidInActiveSection(state, clientUuid);
    const nextBlock = getBlockByUuid(state, nextBlockUuid);
    const isNextBlockNextOrSubmitButton = isPageBreak(nextBlock);
    if (!isNextBlockNextOrSubmitButton && updatedBlock.logic && updatedBlock.logic.action !== 'show') {
      dispatch(addBlock(questionTypes.nextButton, logicQuestionIndex + 1, { scrollToBlock: false }));
    }
  }
};

export const decrementInlineEditToolItemList = (clientUuid) => (dispatch) => {
  const formName = generateInlineEditToolFormName(clientUuid);
  dispatch(arrayPop(formName, 'itemList'));
};

export const incrementInlineEditToolItemList = (clientUuid, index) => (dispatch) => {
  const formName = generateInlineEditToolFormName(clientUuid);
  dispatch(arrayPush(formName, 'itemList', generateLabelItem(index)));
};

export const handleDecrementItemList = (clientUuid) => (dispatch, getState) => {
  const state = getState();
  if (isQuestionLogicValid(state, clientUuid)) {
    dispatch(openChangeScaleItemCount(clientUuid, false));

    return;
  }

  dispatch(decrementInlineEditToolItemList(clientUuid));
};

export const handleIncrementItemList = (clientUuid, index) => (dispatch, getState) => {
  const state = getState();
  if (isQuestionLogicValid(state, clientUuid)) {
    dispatch(openChangeScaleItemCount(clientUuid, true, index));

    return;
  }

  dispatch(incrementInlineEditToolItemList(clientUuid, index));
};

export const modifyBlock = (clientUuid, dataToUpdate = {}) => (dispatch, getState) => {
  const state = getState();
  const modifiedBlockIndex = getBlockIndexInActiveSection(state, clientUuid);
  const updatedBlock = {
    ...getBlockByUuid(state, clientUuid),
    ...dataToUpdate,
  };

  if (questionLogicUtils.isQuestionLogicValid(updatedBlock)) {
    updatedBlock.required = true;
  }

  if (modifiedBlockIndex < 0) {
    return Promise.resolve();
  }
  dispatch(updateBlockRequest(modifiedBlockIndex, updatedBlock));
  dispatch(handleAddLogicNext(clientUuid, modifiedBlockIndex, updatedBlock));
  // @explanation Need to reinitialize form to update initial values from redux state in EditTools redux-form state
  dispatch(
    initialize(
      generateEditLogicToolFormName(clientUuid),
      {
        logic: questionLogicUtils.getQuestionLogic(updatedBlock),
      },
      true,
    ),
  );
  return dispatch(persistBlockUpdateRequest(clientUuid, updatedBlock));
};

export const modifyBlockLogic = (clientUuid, logic) => (dispatch) => {
  dispatch(modifyBlock(clientUuid, { logic }));
};

export const modifyBlockOptions = (clientUuid, options) => (dispatch) => {
  dispatch(modifyBlock(clientUuid, { options }));
};

export const dropQuestionLogic = (questionUuid) =>
  modifyBlockLogic(questionUuid, questionLogicUtils.getDefaultLogic());

export const removeLogicOptionByIndex = (questionUuid, logicListItemIndex) => (dispatch, getState) => {
  const state = getState();
  if (logicListItemIndex !== -1) {
    const questionLogic = getQuestionLogic(state, questionUuid);
    const dataToUpdate = update(questionLogic, {
      logicList: {
        $apply: (logicList) => {
          const filteredList = logicList.filter((listItem, index) => logicListItemIndex !== index);
          return filteredList.length > 0 ? filteredList : questionLogicUtils.defaultLogicList;
        },
      },
    });
    dispatch(modifyBlockLogic(questionUuid, dataToUpdate));
  }
};

export const removeLogicOptionByUuid = (questionUuid, logicOptionUuid) => (dispatch, getState) => {
  const state = getState();
  const block = getBlockByUuid(state, questionUuid);
  const logicListItemIndices = questionLogicUtils.getQuestionLogicListItemIndices(block, logicOptionUuid);
  logicListItemIndices.forEach((logicListItemIndex) =>
    dispatch(removeLogicOptionByIndex(questionUuid, logicListItemIndex)),
  );
};

export const clearQuestionLogicRegardlessOption = (questionUuid) => (dispatch, getState) => {
  const state = getState();
  const questionLogic = getQuestionLogic(state, questionUuid);
  const dataToUpdate = update(questionLogic, {
    regardless: {
      $set: null,
    },
  });

  dispatch(modifyBlockLogic(questionUuid, dataToUpdate));
};

export const clearTiedLogicOptionsToQuestion = (questionUuid, tiedQuestionUuid) => (dispatch, getState) => {
  const state = getState();
  const blockLogicList = getQuestionLogicList(state, questionUuid);
  blockLogicList.forEach((logicItem = {}, index) => {
    const questionToJumpUuid = getQuestionToJumpUuid(logicItem) || null;
    if (questionToJumpUuid === tiedQuestionUuid) {
      dispatch(removeLogicOptionByIndex(questionUuid, index));
    }
  });
};

export const removeTiedQuestionFromLogicOptions = (tiedQuestionUuid) => (dispatch, getState) => {
  const state = getState();
  const surveySectionBlocks = getQuestionsSectionBlocks(state);

  surveySectionBlocks.forEach(({ clientUuid: currentBlockUuid }) => {
    const blockRegardlessValue = getQuestionRegardlessLogicUuid(state, currentBlockUuid);
    if (blockRegardlessValue === tiedQuestionUuid) {
      dispatch(clearQuestionLogicRegardlessOption(currentBlockUuid));
    }

    dispatch(clearTiedLogicOptionsToQuestion(currentBlockUuid, tiedQuestionUuid));
  });
};

export const selectBlock = (clientUuid) => (dispatch) => {
  dispatch(setActiveBlockClientUuid(clientUuid));
  dispatch(setBlockTouched(clientUuid));
};

export const morphBlockType = (nextType, clientUuid) => (dispatch, getState) => {
  const state = getState();
  const currentBlock = getBlockByUuid(state, clientUuid);
  const { id, type: currentType } = currentBlock;

  if (currentType === nextType) return;

  dispatch(cacheBlockData(clientUuid, pickCacheableFields(currentBlock, currentType)));

  const morphedBlock = {
    ...getBlockDefaultProps(nextType),
    id,
    clientUuid,
    type: nextType,
    logic: questionLogicUtils.getDefaultLogic(),
  };

  const cachedBlock = getCachedBlockData(getState(), clientUuid);
  const cacheableFieldsData = pickCacheableFields(cachedBlock, nextType);
  const blockDataToMorph = stubMorphedQuestionByType(nextType, {
    ...morphedBlock,
    ...cacheableFieldsData,
  });

  dispatch(updateBlock(getBlockIndexInActiveSection(getState(), clientUuid), blockDataToMorph));
  dispatch(persistBlockUpdateRequest(clientUuid, blockDataToMorph));
  triggerScrollEvent(); // to update sticky toolbar position @sinslav;
};

export const removeOption = (blockUuid, index) => (dispatch, getState) => {
  const state = getState();
  const filteredOptionList = getQuestionOptionsByUuid(state, blockUuid).filter((option, i) => i !== index);
  dispatch(modifyBlockOptions(blockUuid, filteredOptionList));

  const optionUuid = getBlockOptionIdByIndex(state, blockUuid, index);
  dispatch(removeLogicOptionByUuid(blockUuid, optionUuid));
};

export const handleOptionRemove = (blockUuid, index) => (dispatch, getState) => {
  const state = getState();
  const blockValues = getFormValues(generateInlineEditToolFormName(blockUuid))(state);
  const optionUuid = get(blockValues, ['options', index, 'id']);
  const question = getBlockByUuid(state, blockUuid);
  const isOptionSelectedInLogic = questionLogicUtils.isOptionSelectedInLogic(question, optionUuid);
  if (isOptionSelectedInLogic) {
    dispatch(openDeleteOptionWithLogicModal(blockUuid, index));
    return;
  }

  dispatch(removeOption(blockUuid, index));
};

export const openOverwriteCustomStylesModal = () =>
  openModalDialog({ type: modalTypes.overwriteCustomStyles });

export const openClearSectionModal = (sectionName) =>
  openModalDialog({
    type: modalTypes.clearSection,
    sectionName,
  });

export const openResetActiveSurveySettingsModal = () =>
  openModalDialog({
    type: modalTypes.resetActiveSurveySettings,
  });

export const setLandingEnabledState = (isEnabled) => {
  return (dispatch, getState) => {
    const state = getState();
    const entityId = getEditorEntityId(state);

    dispatch(setSurveyLandingPageEnabled(isEnabled));

    api
      .setLandingVisibility(getActiveEntityCollectionPath(state), entityId, {
        isEnabled,
      })
      .catch((rejection) => {
        dispatch(setSurveyLandingPageEnabled(!isEnabled));
        return handleApiErrorsLegacy(rejection, dispatch);
      });
  };
};

export const persistToggleSurveyFullWidth = (isFullWidth) => {
  return (dispatch, getState) => {
    const state = getState();
    dispatch(toggleSurveyFullWidth(isFullWidth));

    api
      .toggleFullWidth(getActiveEntityCollectionPath(state), getEditorEntityId(state), { isFullWidth })
      .catch((rejection) => {
        dispatch(toggleSurveyFullWidth(!isFullWidth));
        return handleApiErrorsLegacy(rejection, dispatch);
      });
  };
};

export const enableLanding = () => setLandingEnabledState(true);

export const toggleLandingEnabled = () => {
  return (dispatch, getState) => {
    const landingEnabled = isSurveyLandingEnabled(getState());
    dispatch(setLandingEnabledState(!landingEnabled));
  };
};

export const setThankYouEnabledState = (isEnabled) => {
  return (dispatch, getState) => {
    const state = getState();

    dispatch(setSurveyThankYouPageEnabled(isEnabled));

    if (isEnabled) {
      api
        .setThankYouVisibility(getActiveEntityCollectionPath(state), getEditorEntityId(state), { isEnabled })
        .catch((rejection) => {
          dispatch(setSurveyThankYouPageEnabled(!isEnabled));
          return handleApiErrorsLegacy(rejection, dispatch);
        });
    }
  };
};

export const enableThankYou = () => setThankYouEnabledState(true);

export const persistThankYouRedirectUrl = (redirectUrl) => {
  return (dispatch, getState) => {
    const state = getState();
    const endEnabled = isSurveyThankYouEnabled(state);

    dispatch(handleSurveyRedirectUrlChange(redirectUrl));

    return api
      .setThankYouVisibility(getActiveEntityCollectionPath(state), getEditorEntityId(state), {
        isEnabled: endEnabled,
        redirectUrl,
      })
      .catch((rejection) => handleApiErrorsLegacy(rejection, dispatch));
  };
};

export const toggleThankYouEnabled = () => {
  return (dispatch, getState) => {
    const state = getState();
    const endEnabled = isSurveyThankYouEnabled(state);
    const redirectUrl = getSurveyRedirectUrl(state);

    dispatch(setThankYouEnabledState(!endEnabled));

    if (endEnabled && redirectUrl) {
      dispatch(persistThankYouRedirectUrl(redirectUrl));
    }
  };
};

export const navigateToSurveyList = () => (dispatch) => {
  dispatch(navigate(''));
};

export const showCurrentSurveySuccessfulCreationNotification = () => (dispatch, getState) => {
  const surveyName = getEditorSurveyName(getState());
  dispatch(showSuccessNotification(`<b>${surveyName}</b> was successfully saved.`));
};

export const finishCurrentSurveyCreation = () => (dispatch) => {
  dispatch(navigateToSurveyList());
  dispatch(showCurrentSurveySuccessfulCreationNotification());
};

export const navigateToSection = (next) => (dispatch, getState) => {
  const state = getState();
  const surveyId = getEditorEntityId(state);
  const endEnabled = isSurveyThankYouEnabled(state);
  const redirectUrl = getSurveyRedirectUrl(state);
  const redirectUrlErrors = validateRedirectUrl(endEnabled, redirectUrl);

  if (redirectUrlErrors.length) {
    dispatch(
      openApplicationErrorModal({
        heading: 'Redirect URL issues that need fixing (Thank you page section):',
        list: redirectUrlErrors,
      }),
    );
    return;
  }
  if (next) {
    dispatch(navigate(`${getCurrentRouterRootPath(state)}/${surveyId}/${next}`));
  }
};

export const destroyAllQuestionEditToolForms = () => (dispatch, getState) => {
  const state = getState();
  const lastBlock = last(getActiveSectionBlocks(state));
  const requiredBlocksUuid =
    lastBlock && isPageBreak(lastBlock) ? [lastBlock.clientUuid, lastBlock.backButton.clientUuid] : [];
  const excludeRequiredForms = (formName) =>
    !requiredBlocksUuid.some((requiredBlockUuid) => includes(formName, requiredBlockUuid));

  const formsToDestroy = [
    ...getEditToolFormNames(state),
    ...getLogicEditToolFormNames(state),
    ...getInlineEditToolFormNames(state),
    ...getInlineLabelEditorFormNames(state),
  ].filter(excludeRequiredForms);

  dispatch(destroy(...formsToDestroy));
};

export const deleteSection = (section) => (dispatch, getState) => {
  const state = getState();
  const entityId = getEditorEntityId(state);
  const fallBackBlockList = getActiveSectionBlocks(state);
  dispatch(clearSection(section));
  dispatch(destroyAllQuestionEditToolForms());
  dispatch(updateOrderMap());

  return api.deleteSection(getActiveEntityCollectionPath(state), entityId, { section }).catch((rejection) => {
    dispatch(returnUndeleteBlockList(section, fallBackBlockList));
    return handleApiErrorsLegacy(rejection, dispatch);
  });
};

export const clearSectionWithHeader = (sectionName) => (dispatch) =>
  Promise.all([dispatch(deleteSection(sectionName)), dispatch(removeSectionHeader(sectionName))]);

export const setActiveSection = (sectionName) => (dispatch) => {
  dispatch(batchActions([updateActiveSection(sectionName), updateOrderMap()]));
};

export const setSurveyName = createAction(Constants.SET_EDITOR_SURVEY_NAME);

export const changeSurveyName = (name) => (dispatch, getState) => {
  const state = getState();
  dispatch(setIsSurveyNameFocused(false));
  if (name === getEditorSurveyName(getState())) {
    return Promise.resolve();
  }

  return api
    .changeName(getActiveEntityCollectionPath(state), getEditorEntityId(state), { name })
    .catch((rejection) => {
      dispatch(setIsSurveyNameFocused(true));
      return handleApiErrorsLegacy(rejection, dispatch);
    })
    .then(() => {
      dispatch(setSurveyName(name));
    });
};

export const changeSurveySlug = (slug) => (dispatch, getState) => {
  const state = getState();
  const prevSlug = getSurveySlug(state);
  if (slug === prevSlug) {
    return Promise.resolve();
  }

  dispatch(updateShareUrlSlug(slug));

  return api.changeSurveySlug(getEditorEntityId(state), { slug }).catch((rejection) => {
    dispatch(updateShareUrlSlug(prevSlug));
    return handleApiErrorsLegacy(rejection, dispatch);
  });
};

export const revertUrlSlug = (slug) => (dispatch) => {
  dispatch(changeForm(formNames.changeSurveySlug, 'slug', slug));
};

export const copySurveyLink = () => (dispatch, getState) => {
  dispatch(copyLinkToClipboard(getSurveyTakerUrl(getState())));
};

export const openInsertQuestionOptionImageModal = ({ form, field }) =>
  openModalDialog({
    type: modalTypes.insertQuestionOptionImage,
    field,
    form,
  });

export const changeQuestionOptionImage = ({ form, field, image }) => (dispatch, getState) => {
  const fieldValue = formValueSelector(form)(getState(), field);
  dispatch(
    changeForm(form, field, {
      ...fieldValue,
      src: image.url,
      imageId: image.id,
    }),
  );
};

export const removeOptionImageFromQuestion = ({ form, field }) =>
  changeQuestionOptionImage({
    form,
    field,
    image: {
      src: null,
      imageId: null,
    },
  });

export const insertQuestionOptionImageFromPreview = ({ form, field, parentDialogId }) => (image) => (
  dispatch,
) => {
  dispatch(closeModalDialog(parentDialogId));
  return dispatch(
    changeQuestionOptionImage({
      form,
      field,
      image,
    }),
  );
};

export const openPostLogicNotAvailableModal = () =>
  openModalDialog({
    type: modalTypes.applicationError,
    title: 'Sorry, not allowed!',
    heading: 'You cannot configure post survey logic unless:',
    list: [
      'Landing Page is turned ON',
      'Email address field is required',
      'Scale, Numeric, Single Choice, or Dropdown questions exist',
    ],
    okButtonText: 'Ok',
  });

export const openPostSurveyLogic = () => (dispatch, getState) => {
  if (!canOpenPostSurveyLogic(getState())) {
    return dispatch(openPostLogicNotAvailableModal());
  }

  const url = getUrl.section({
    root: surveyEditorRoot,
    entityId: getEditorEntityId(getState()),
    sectionName: mapEditorSectionToUrl.postSurveyLogic,
  });
  return dispatch(navigate(url));
};

export const initializeCutOffDateForm = (values) => initialize(formNames.cutOffDate, values);

export const clearCutOffDateFormErrors = () => clearSubmitErrors(formNames.cutOffDate);

export const initPreview = () => (dispatch, getState) => {
  const state = getState();
  const {
    id,
    name,
    endEnabled,
    landingEnabled,
    redirectUrl,
    isFullWidth,
    headerEndEnabled,
    headerLandingEnabled,
    headerQuestionsEnabled,
    headerUuid,
  } = getEditorRawSurveyData(state);
  const header = getEditorHeaderData(state);
  const blockList = getEditorOrderedBlockList(state);
  const isBackButtonEnabled = isSurveyBackButtonEnabled(state);

  dispatch(
    batchActions([
      setPreviewSurveyBlockList(blockList),
      setPreviewSectionHeaders(header),
      setPreviewSurveySharedHeader(headerUuid),
      setPreviewSurveySettings({
        id,
        name,
        endEnabled,
        landingEnabled,
        redirectUrl,
        isFullWidth,
        headerEndEnabled,
        headerLandingEnabled,
        headerQuestionsEnabled,
        isBackButtonEnabled,
      }),
    ]),
  );

  const latestVisitedPage = getSurveyPreviewLatestVisitedPageNumber(state);
  dispatch(setCurrentPreviewPage(latestVisitedPage || 0));
};

export const goToPreview = () => (dispatch, getState) => {
  const state = getState();
  const surveyId = getEditorEntityId(state);
  resetSurveyEditorPersistedState(surveyId);

  dispatch(initPreview());
  dispatch(
    navigate(
      `${getCurrentRouterRootPath(state)}/${surveyId}/${mapEditorSectionToUrl.preview}/${numbering.toHuman(
        getPageToStartPreview(state),
      )}`,
    ),
  );
};

export const resetActiveSurveySettings = () => (dispatch, getState) => {
  const settingsFallback = getActiveSurveySettings(getState());
  const defaultSettings = omit(DefaultSurveySettings, 'postLogicQuestionsCount');
  dispatch(changeSurveySettings(defaultSettings));

  // IMPORTANT: Destroy frequencyTime forms because when switching between them,
  // their state should be consistent by default
  const surveyId = getEditorEntityId(getState());
  [formNames.frequencyTimeDay, formNames.frequencyTimeWeek, formNames.frequencyTimeMonth].map((formName) =>
    dispatch(destroy(formName + surveyId)),
  );

  return dispatch(persistSurveySettings(defaultSettings))
    .then(() => {
      dispatch(showSuccessNotification('Settings were reset successfully.'));
    })
    .catch(() => dispatch(changeSurveySettings(settingsFallback)));
};

const initGeneralBuilder = (entity) => (dispatch, getState) => {
  dispatch(setActiveSurvey(setReportSettingsTimezone(entity, getUserTimeZone(getState()))));

  dispatch(setQuestionNumbers());
  dispatch(setSectionHeaders({}));
  map(entity.headers, (header) => dispatch(applySectionHeaders(get(header, 'sectionAssignee'), header)));
  dispatch(resetRichTextEditor());
  dispatch(updateOrderMap());
  dispatch(restorePreview());
  dispatch(initPreview());
  dispatch(syncPreviewThumbnail(entity));
};

export const initSurveyBuilder = (survey) => (dispatch) => {
  dispatch(initGeneralBuilder(survey));
  dispatch(resetPostSurveyLogic());
  dispatch(updateShareUrlSlug(survey.slug));
};

export const loadTemplate = (id, extraParams) => (dispatch) => {
  dispatch(setEditorLoadingState(true));

  return api
    .getTemplate(id, extraParams)
    .catch((rejection) => {
      dispatch(setEditorLoadingState(false));
      return handleApiErrorsLegacy(rejection, dispatch);
    })
    .then((template) => {
      dispatch(initGeneralBuilder(template));
      dispatch(setEditorLoadingState(false));
    });
};

export const loadSurvey = (id, extraParams) => (dispatch) => {
  dispatch(setEditorLoadingState(true));

  return api
    .getSurvey(id, extraParams)
    .catch((rejection) => {
      dispatch(setEditorLoadingState(false));
      return handleApiErrorsLegacy(rejection, dispatch);
    })
    .then((survey) => {
      dispatch(initSurveyBuilder(survey));
      dispatch(setEditorLoadingState(false));
    });
};

export const saveSurveyAsTemplate = (entityId) => (dispatch) =>
  api
    .cloneSurvey(entityId, { type_: SURVEY_TEMPLATE_TYPE })
    .catch(() =>
      dispatch(showErrorNotification('Cannot save as template, something went wrong. Try again later')),
    )
    .then((data) =>
      dispatch(
        showSuccessNotification(
          `New template
      <a href="${editorPath}${surveyTemplateEditorRoot}/${data.id}"><b>${data.name}</b></a>
      was successfully saved`,
        ),
      ),
    );

export const openLoadTemplateModal = () =>
  openModalDialog({
    type: modalTypes.useSurveyTemplate,
  });

export const useTemplateForSurvey = (surveyId, templateId) => (dispatch) => {
  dispatch(setEditorLoadingState(true));

  return api
    .useSurveyForTemplate(surveyId, templateId)
    .catch((rejection) => {
      dispatch(setEditorLoadingState(false));
      return dispatch(handleApiErrors(rejection));
    })
    .then((survey) => {
      dispatch(initSurveyBuilder(survey));
      dispatch(setEditorLoadingState(false));
    });
};

export const openUseTemplateConfirmationModal = (templateId, parentDialogId) => (dispatch, getState) => {
  const surveyId = getEditorEntityId(getState());

  return dispatch(
    openModalDialog({
      type: modalTypes.confirmUseSurveyTemplate,
      surveyId,
      templateId,
      parentDialogId,
    }),
  );
};

export const goToSurveyEmbedding = () => (dispatch, getState) => {
  const url = getUrl.section({
    root: surveyEditorRoot,
    entityId: getEditorEntityId(getState()),
    sectionName: mapEditorSectionToUrl.surveyEmbedding,
  });

  return dispatch(navigate(url));
};

export const changeEditSurveyStatus = createAction(Constants.CHANGE_EDIT_SURVEY_STATUS, (status) => ({
  status,
}));

export const updateEditSurveyStatus = createAction(Constants.UPDATE_EDIT_SURVEY_STATUS, (status) => ({
  status,
}));

export const setSurveyStatusPendingState = createAction(Constants.SET_SURVEY_STATUS_PENDING_STATE);

export const openSetSharedHeaderModal = () =>
  openModalDialog({
    type: modalTypes.setSharedHeader,
  });

export const toggleSurveySharedHeader = (checked) => (dispatch, getState) => {
  let action = setSharedHeader(null);

  if (checked) {
    const state = getState();
    const sectionName = getActiveEditorSection(state);
    const { clientUuid } = getEditorSectionHeader(state, sectionName);
    action = setSharedHeader(clientUuid);
  }
  dispatch(action);
};

export const persistSurveySharedHeader = (sharedHeaderUuid) => (dispatch, getState) => {
  const state = getState();
  const { headerUuid: prevSharedHeaderUuid } = getEditorRawSurveyData(state);
  dispatch(setSharedHeader(sharedHeaderUuid));

  api
    .changeSurveySetting(getActiveEntityCollectionPath(state), getEditorEntityId(state), {
      headerUuid: sharedHeaderUuid,
    })
    .catch((rejection) => {
      dispatch(setSharedHeader(prevSharedHeaderUuid));
      return handleApiErrorsLegacy(rejection, dispatch);
    });
};

export const persistToggleSurveySharedHeader = (isHeaderShared) => (dispatch, getState) => {
  const state = getState();

  let sharedHeaderUuid = null;
  if (isHeaderShared) {
    const sectionName = getActiveEditorSection(state);
    const { clientUuid } = getEditorSectionHeader(state, sectionName);
    sharedHeaderUuid = clientUuid;
  }
  dispatch(persistSurveySharedHeader(sharedHeaderUuid));
};

export const persistSurveyEndSectionHeaderEnabled = (value) => (dispatch) => {
  dispatch(setSurveyHeaderEndEnabled(value));
  dispatch(persistSurveySetting('headerEndEnabled', value));
};

export const persistSurveyLandingSectionHeaderEnabled = (value) => (dispatch) => {
  dispatch(setSurveyHeaderLandingEnabled(value));
  dispatch(persistSurveySetting('headerLandingEnabled', value));
};

export const persistSurveyQuestionsSectionHeaderEnabled = (value) => (dispatch) => {
  dispatch(setSurveyHeaderQuestionsEnabled(value));
  dispatch(persistSurveySetting('headerQuestionsEnabled', value));
};

export const persistReplaceSurveyBlock = (block) => (dispatch) => {
  dispatch(replaceSurveyBlock(block));
  dispatch(persistBlockUpdateRequest(block.clientUuid, block));
};
